import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { NotificationDto } from 'generated/openApi';
import ApiService from 'services/apiService';

interface NotificationDataContextValue {
  newNotifications: NotificationDto[];
  dismissedNotifications: NotificationDto[];
  loading: boolean;
  error: string | null;
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  fetchNewNotifications: (page?: number, pageSize?: number) => Promise<void>;
  fetchDismissedNotifications: (page?: number, pageSize?: number) => Promise<void>;
  refetchAll: () => Promise<void>;
}

const NotificationDataContext = createContext<NotificationDataContextValue | undefined>(undefined);

export const NotificationDataProvider = ({ children }: { children: ReactNode }) => {
  const [newNotifications, setNewNotifications] = useState<NotificationDto[]>([]);
  const [dismissedNotifications, setDismissedNotifications] = useState<NotificationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNewNotifications = async (page: number = 1, pageSize: number = 5) => {
    try {
      setLoading(true);
      setError(null);
      const resp = await ApiService.notifications.getList(
        true,    // unreadOnly
        null,    // readOnly
        page,
        pageSize
      );
      setNewNotifications(resp.notifications ?? []);
    } catch (err) {
      console.error('Error fetching new notifications:', err);
      setError('Failed to load new notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchDismissedNotifications = async (page: number = 1, pageSize: number = 5) => {
    try {
      setLoading(true);
      setError(null);
      const resp = await ApiService.notifications.getList(
        null,    // unreadOnly
        true,    // readOnly
        page,
        pageSize
      );
      setDismissedNotifications(resp.notifications ?? []);
    } catch (err) {
      console.error('Error fetching dismissed notifications:', err);
      setError('Failed to load dismissed notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewNotifications(1, 5);
  }, []);

  const markAsRead = async (notificationId: number) => {
    try {
      await ApiService.notifications.markAsRead(notificationId);
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await ApiService.notifications.markAllAsRead();
      await refetchAll();
    } catch (err) {
      console.error('Error marking all as read:', err);
      setError('Failed to mark notifications as read');
    }
  };

  const refetchAll = async () => {
    await Promise.all([fetchNewNotifications(1, 5), fetchDismissedNotifications(1, 5)]);
  };

  return (
    <NotificationDataContext.Provider
      value={{
        newNotifications,
        dismissedNotifications,
        loading,
        error,
        markAsRead, 
        markAllAsRead,
        fetchNewNotifications,
        fetchDismissedNotifications,
        refetchAll,
      }}
    >
      {children}
    </NotificationDataContext.Provider>
  );
};

export const useNotificationData = () => {
  const context = useContext(NotificationDataContext);
  if (!context) {
    throw new Error('useNotificationData must be used within NotificationDataProvider');
  }
  return context;
};